import { Box, DatePicker, Flex, IconicButton, QuietButton, Select, Text } from "@nulogy/components";
import * as Sentry from "@sentry/react";
import SplitButton from "components/controls/SplitButton";
import {
  QuickNavigationOption,
  QuickNavigationUserOptions,
  QuickNavigationUserOptionValue,
} from "config/quickNavigationOptions";
import { defaultZoomLevel, zoomOptions } from "config/zoomConfig";
import { useSchedulerState } from "hooks/state";
import React from "react";
import styled, { useTheme } from "styled-components";
import DateTime from "utils/DateTime";
import ExtendedViewToggle from "./ExtendedViewToggle";
import ZoomLevelSelector from "./ZoomLevelSelector";

const BorderlessDatePickerWrapper = styled.span(({ theme }) => ({
  ".react-datepicker-popper": {
    zIndex: "100000 !important",
  },
  display: "inline-block",

  input: {
    border: "none",
    marginTop: 0,
    paddingLeft: theme.space.x4,
    paddingRight: "7px",
    width: "92px",
  },
  position: "relative",

  "svg.nds-icon--calendarToday": {
    left: theme.space.x1,
  },
}));

const RangeTypeSelector = styled(Select)`
  width: 108px;

  @media only screen and (max-width: 825px) {
    width: 100%;
  }

  border: none;
  div[data-testid="select-control"] > div {
    border: transparent;
  }
  div[data-testid="select-control"] > div.nds-select--is-focused {
    border: 1px solid ${({ theme }) => theme.colors.blue};
    border-radius: ${({ theme }) => theme.sizes.half};
  }
`;

const BorderedContainer = styled(Flex)`
  border-radius: 4px 4px 4px 4px;
  border: 1px solid ${({ theme }) => theme.colors.grey};
  /* Opaque, so the cluster reads the same on the spark shell's canvas as it does on the plain page: the
     date controls are transparent and would otherwise show the canvas while the range select stays white. */
  background-color: ${({ theme }) => theme.colors.white};
  margin-right: ${({ theme }) => theme.space.x2};

  @media only screen and (max-width: 825px) {
    margin-right: 0;
  }
`;

const HiddenText = styled(Text)`
  visibility: hidden;
`;

const EndDateBox = styled(Box)`
  min-width: 56px;
  width: 100%;
`;

const Divider = () => {
  const theme = useTheme();

  return <Box borderRight={`1px solid ${theme.colors.grey}`} height="24px" width="1px" />;
};

const FlexContainer = styled(Flex)`
  @media only screen and (max-width: 825px) {
    flex-direction: column;
    align-items: start;
    gap: 8px;
    flex-grow: 1;
  }
`;

const RangeScrollFlex = styled(Flex)`
  width: 100%;
`;

function formatDate(endDate) {
  return DateTime.format(endDate, "MMM DD");
}

export default function ZoomPicker({
  disabled,
  onDateChange,
  onQuickNavigationChange,
  onZoomLevelChange,
  zoomLevel,
  visibleTimeRange,
}) {
  const {
    state: { quickNavigationOption },
  } = useSchedulerState();
  const handleResetZoom = () => {
    onZoomLevelChange(defaultZoomLevel);
  };

  let isOnQuickNavigationDate;
  switch (quickNavigationOption) {
    case QuickNavigationUserOptionValue.WEEK_START:
      isOnQuickNavigationDate = DateTime.isStartOfWeek(visibleTimeRange.startDate);
      break;
    case QuickNavigationUserOptionValue.TODAY:
    case QuickNavigationUserOptionValue.NOW:
      isOnQuickNavigationDate = DateTime.isToday(visibleTimeRange.startDate);
      break;
    default:
      Sentry.captureMessage(`Unexpected quickNavigationOption: : ${quickNavigationOption}`, {});
      isOnQuickNavigationDate = false;
  }

  const isDatesTheSame = DateTime.isSameDate(visibleTimeRange.startDate, visibleTimeRange.endDate);

  const EndDateText = isDatesTheSame ? HiddenText : Text;

  const handlePreviousClick = () => {
    onQuickNavigationChange(QuickNavigationOption.BACK);
  };
  const handleNextClick = () => {
    onQuickNavigationChange(QuickNavigationOption.FORWARD);
  };

  return (
    <FlexContainer alignItems="flex-start">
      <BorderedContainer alignItems="center" gap="x1" px="x1" width="100%">
        <SplitButton
          disabled={disabled}
          onChange={onQuickNavigationChange}
          options={QuickNavigationUserOptions}
          quiet={isOnQuickNavigationDate}
          selectedOption={quickNavigationOption}
        />

        <BorderlessDatePickerWrapper>
          <DatePicker
            className="spec-current-date-picker"
            dateFormat={["MMM dd", "MMM d, yyyy"]}
            name="placeholder"
            onChange={onDateChange}
            selected={new Date(visibleTimeRange.startDate)}
          />
        </BorderlessDatePickerWrapper>
        <EndDateText color="midGrey">–</EndDateText>
        <EndDateBox ml="x1">
          <EndDateText className="spec-end-date-text" color="midGrey">
            {formatDate(visibleTimeRange.endDate)}
          </EndDateText>
        </EndDateBox>

        <Divider />
        <IconicButton
          className="spec-previous-date-click"
          disabled={disabled}
          icon="leftArrow"
          mr="0px"
          onClick={handlePreviousClick}
        />
        <IconicButton
          className="spec-next-date-click"
          disabled={disabled}
          icon="rightArrow"
          ml="0px"
          onClick={handleNextClick}
        />
      </BorderedContainer>

      <RangeScrollFlex flexGrow={1} width="100%">
        <BorderedContainer alignItems="center" gap="x1" px="x1" width="100%">
          <RangeTypeSelector
            className="spec-zoom-select"
            disabled={disabled}
            id="date-range-type"
            maxHeight="400px"
            onChange={onZoomLevelChange}
            options={zoomOptions}
            value={zoomLevel}
          />
          <Divider />
          <ZoomLevelSelector disabled={disabled} onZoomLevelChange={onZoomLevelChange} zoomLevel={zoomLevel} />
          <Divider />

          <QuietButton disabled={disabled} onClick={handleResetZoom} size="small">
            Reset
          </QuietButton>
        </BorderedContainer>
      </RangeScrollFlex>

      {/* Its own frame: it governs how the schedule scrolls rather than what the range and zoom show, and
          unlike everything beside it, it is saved for the user. */}
      <BorderedContainer alignItems="center" flexShrink="0" px="x1">
        <ExtendedViewToggle disabled={disabled} />
      </BorderedContainer>
    </FlexContainer>
  );
}
